import { Col, Radio, Row, Select, Typography } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { DefaultOptionType } from 'antd/lib/select';
import React, { useMemo } from 'react';

import { BankAccountType } from '@portal/enum/accountType';
import { translate } from '@portal/services/i18n';
import { removeAccent } from '@portal/services/masks';

import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import useReduxState from '@portal/hooks/useReduxState';
import { keepOnlyNumbers } from '@portal/utils/format';

export interface ExternalFavoredFormProps {
  banks?: models.Bank[];
  bankAccount: string;
  setBankAccount: (value: string) => void;
  bankAccountDigit: string;
  setBankAccountDigit: (value: string) => void;
  bankAgency: string;
  setBankAgency: (value: string) => void;
  setBankCode?: (value: string) => void;
  bankCode?: string;
  bankUserName?: string;
  setBankUserName: (value: string) => void;
  accountType: BankAccountType;
  setAccountType: (value: BankAccountType) => void;
  bankCodeType?: 'PSP' | 'CODE';
  pspList?: bank.Psp[];
  setPspCode?: (value: string) => void;
  pspCode?: string;
}

const ExternalFavoredForm = ({
  banks,
  bankAccount,
  setBankAccount,
  bankAccountDigit,
  setBankAccountDigit,
  bankAgency,
  setBankAgency,
  setBankCode,
  bankCode,
  bankUserName,
  setBankUserName,
  accountType,
  setAccountType,
  bankCodeType,
  pspList,
  setPspCode,
  pspCode,
}: ExternalFavoredFormProps) => {
  const {
    paymentAccount: { selectedPaymentAccount },
  } = useReduxState();

  const filterOption = (input: string, option?: DefaultOptionType) =>
    removeAccent(option?.children ? option.children.toString() : '')
      .toLowerCase()
      .indexOf(removeAccent(input).toLowerCase()) >= 0;

  const handleBankSelected = (value: string) => {
    if (!setBankCode || !banks) {
      return;
    }

    const selectedBank = banks.find((bank) => bank.code === value);
    if (selectedBank) {
      setBankUserName(selectedBank.name);
      setBankCode(value);
    } else {
      setBankUserName('');
      setBankCode('');
    }
  };

  const handleSelectedPsp = (value: string) => {
    if (!setPspCode || !pspList) {
      return;
    }

    const bank = pspList.find((psp) => psp.id === value);
    if (bank) {
      setBankUserName(bank.name);
      setPspCode(value);
    } else {
      setBankUserName('');
      setPspCode('');
    }
  };

  const banksValue = useMemo(() => {
    const bankOptions =
      banks
        ?.filter(
          (bank) =>
            bank.code &&
            bank.code !== selectedPaymentAccount?.bankNumber &&
            bank
        )
        .map((bank) => (
          <Select.Option value={bank.code} key={bank.code}>
            {bank.code} - {bank.name}
          </Select.Option>
        )) || [];

    if (
      bankCode &&
      !banks?.find((bank) => bank.code === bankCode) &&
      bankUserName
    ) {
      bankOptions.push(
        <Select.Option value={bankCode} key={bankCode}>
          {bankCode} - {bankUserName}
        </Select.Option>
      );
    }

    return bankOptions;
  }, [banks, selectedPaymentAccount, bankCode, bankUserName]);

  return (
    <>
      <p className="external-favored-form__account-type">
        <b>
          {translate('COMPONENTS.EXTERNAL_FAVORED_FORM.ACCOUNT_TYPE.LABEL')}
        </b>
      </p>

      <Radio.Group
        value={accountType}
        onChange={(e: RadioChangeEvent) => setAccountType(e.target.value)}
        className="external-favored-form__account-type__radioButton"
      >
        <Radio
          value={BankAccountType.CURRENT}
          className="external-favored-form__account-type__radioButton__option"
        >
          {translate('COMPONENTS.EXTERNAL_FAVORED_FORM.ACCOUNT_TYPE.CURRENT')}
        </Radio>
        <Radio
          value={BankAccountType.SAVINGS}
          className="external-favored-form__account-type__radioButton__option"
        >
          {translate('COMPONENTS.EXTERNAL_FAVORED_FORM.ACCOUNT_TYPE.SAVINGS')}
        </Radio>
      </Radio.Group>

      {bankCodeType === 'PSP' ? (
        <Row>
          <Col className="external-favored-form__input">
            <Select
              className="external-favored-form__input__select"
              showSearch
              allowClear
              bordered={false}
              placeholder={translate('COMPONENTS.EXTERNAL_FAVORED_FORM.PSP')}
              filterOption={filterOption}
              onChange={handleSelectedPsp}
              value={pspCode ?? null}
            >
              {pspList?.length
                ? pspList?.map((psp) => (
                    <Select.Option value={psp.id} key={psp.id}>
                      {psp.code ? psp.code : psp.id} - {psp.name}
                    </Select.Option>
                  ))
                : null}
              {pspCode &&
              !pspList?.find((psp) => psp.id === pspCode) &&
              bankUserName ? (
                <Select.Option value={pspCode} key={pspCode}>
                  {pspCode} - {bankUserName}
                </Select.Option>
              ) : null}
            </Select>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col className="external-favored-form__input">
              <Select
                className="external-favored-form__input__select"
                showSearch
                allowClear
                bordered={false}
                placeholder={translate(
                  'COMPONENTS.EXTERNAL_FAVORED_FORM.BANK_NAME'
                )}
                filterOption={filterOption}
                onChange={handleBankSelected}
                value={bankCode || null}
              >
                {banksValue}
              </Select>
            </Col>
          </Row>
        </>
      )}
      <Row className="external-favored-form__input">
        <Col span={7}>
          <AdvancedInput
            variant="standard"
            type="tel"
            label={translate('COMPONENTS.EXTERNAL_FAVORED_FORM.AGENCY')}
            maxLength={4}
            value={bankAgency}
            onChange={(value) => setBankAgency(keepOnlyNumbers(value))}
          />
        </Col>
        <Col span={7}>
          <AdvancedInput
            variant="standard"
            type="tel"
            label={translate('COMPONENTS.EXTERNAL_FAVORED_FORM.ACCOUNT')}
            maxLength={13}
            value={bankAccount}
            onChange={(value) => setBankAccount(keepOnlyNumbers(value))}
          />
        </Col>
        <Col span={7}>
          <AdvancedInput
            variant="standard"
            type="tel"
            label={translate('COMPONENTS.EXTERNAL_FAVORED_FORM.DIGIT')}
            maxLength={1}
            value={bankAccountDigit}
            onChange={(value) => setBankAccountDigit(keepOnlyNumbers(value))}
          />
          <Typography.Text
            type="secondary"
            className="external-favored-form__digit-hint"
          >
            {translate('COMPONENTS.EXTERNAL_FAVORED_FORM.DIGIT_HINT')}
          </Typography.Text>
        </Col>
      </Row>
    </>
  );
};

export default ExternalFavoredForm;
